import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { createContext, useContext, useEffect, ReactNode } from 'react';
import { ChannelType } from '@type/channelType';
import { PostResponse } from '../../../response/responseSchema';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import firebaseStore from '@client/stores/firebaseStore';
import webViewStore from '@client/stores/webViewStore';

class PostStore {
    readonly channelType: ChannelType;
    readonly postHash: string;

    post: PostResponse | null = null;
    isLoading = true;

    constructor(channelType: ChannelType, postHash: string) {
        makeAutoObservable(this);
        this.channelType = channelType;
        this.postHash = postHash;
    }

    /**
     * 현재 사용자가 포스트 작성자인지 확인
     */
    get isAuthor() {
        return this.post && firebaseStore.me && this.post.author.hash === firebaseStore.me.hash;
    }

    /**
     * 포스트 조회
     */
    async fetchPost(isForce = false) {
        this.isLoading = true;

        try {
            if (this.channelType && this.postHash) {
                const postResult = await apiClient.post.public.getPostByHash.$query(
                    {
                        channelType: this.channelType,
                        postHash: this.postHash,
                    },
                    {
                        force: isForce,
                    },
                );

                runInAction(() => {
                    this.post = postResult;
                });
            }
        } catch (error) {
            console.error('Failed to fetch post:', error);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
}

const PostContext = createContext<PostStore | null>(null);

export const PostProvider = observer(
    ({
        channelType,
        postHash,
        children,
    }: {
        channelType: ChannelType;
        postHash: string;
        children: ReactNode;
    }) => {
        const postStore = useMobxInstance(PostStore, channelType, postHash);

        useEffect(() => {
            postStore.fetchPost();
        }, [postStore]);

        useEffect(() => {
            if (!webViewStore.isWebView) {
                return;
            }

            const onPostEdited = async (data: { channelType: ChannelType; postHash: string }) => {
                if (data.channelType === channelType && data.postHash === postHash) {
                    await postStore.fetchPost(true);
                }
            };

            webViewStore.addSyncUiHandler('postEdited', onPostEdited);

            return () => {
                webViewStore.removeSyncUiHandler('postEdited', onPostEdited);
            };
        }, [postStore, webViewStore.isWebView]);

        return <PostContext.Provider value={postStore}>{children}</PostContext.Provider>;
    },
);

/**
 * 포스트 컨텍스트 사용 훅
 */
export const usePostStore = (): PostStore => {
    const postStore = useContext(PostContext);

    if (!postStore) {
        throw new Error('usePostStore must be used within a PostProvider');
    }

    return postStore;
};
